import { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Package, ArrowRight, Warning } from "@phosphor-icons/react";

export default function MaterialPicking() {
  const [pending, setPending] = useState([]);
  const [picked, setPicked] = useState([]);
  const [orderId, setOrderId] = useState("");
  const [approxQty, setApproxQty] = useState("");
  const [remarks, setRemarks] = useState("");
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data: pend }, { data: all }] = await Promise.all([
      client.get("/orders/pending-picking"),
      client.get("/orders"),
    ]);
    setPending(pend);
    setPicked(all.filter((o) => o.picking && ["picking_done","scanned","packed","dispatched"].includes(o.stage)).slice(0, 20));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!orderId) return toast.error("Select an order");
    if (approxQty === "" || Number(approxQty) < 0) return toast.error("Enter Approx Box Quantity (0 if no stock)");
    try {
      await client.post("/orders/pick", {
        order_id: orderId,
        approx_quantity: Number(approxQty),
        remarks,
      });
      toast.success("Order picked"); setOrderId(""); setApproxQty(""); setRemarks(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Material Picking" subtitle="Stage 2 · Warehouse" />
      <Section>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-500 rounded-sm p-5">
            <div className="label-cap text-amber-800">New Picking Entry</div>
            <h3 className="font-display text-xl font-bold mt-1 text-amber-900 mb-4">Pick an order</h3>

            <label className="label-cap">Order No</label>
            <Select value={orderId} onValueChange={setOrderId}>
              <SelectTrigger data-testid="picking-order-select" className="rounded-sm mt-1 bg-white"><SelectValue placeholder="Select pending order"/></SelectTrigger>
              <SelectContent>
                {pending.map(o => <SelectItem key={o.id} value={o.id}>{o.order_no} — {o.retailer_name}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="label-cap block mt-4">Approx Box Quantity</label>
            <Input data-testid="picking-approx-input" type="number" min="0" value={approxQty} onChange={(e)=>setApproxQty(e.target.value)} className="mt-1 bg-white"/>
            <div className="flex items-start gap-1 mt-1 text-xs text-amber-700"><Warning size={13} className="mt-0.5 shrink-0"/> Enter <strong>0</strong> if NO STOCK</div>

            <label className="label-cap block mt-4">Remarks</label>
            <Textarea data-testid="picking-remarks-input" value={remarks} onChange={(e)=>setRemarks(e.target.value)} rows={2} className="mt-1 bg-white"/>

            <Button data-testid="picking-submit-btn" onClick={submit} className="w-full mt-4 bg-amber-600 hover:bg-amber-700 rounded-sm">
              <Package size={16} weight="bold" className="mr-1"/> Mark as Picked <ArrowRight size={16} className="ml-1"/>
            </Button>
          </div>

          <div className="lg:col-span-2">
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-5 py-3 border-b border-slate-200"><div className="label-cap">Pending Picking · {pending.length}</div></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2">Order No</th><th className="px-4 py-2">Company</th>
                    <th className="px-4 py-2">Retailer</th><th className="px-4 py-2">Order Qty</th><th className="px-4 py-2">Ordered</th>
                  </tr></thead>
                  <tbody>
                    {pending.map(o=>(
                      <tr key={o.id} className="border-b border-slate-100 hover:bg-amber-50">
                        <td className="px-4 py-2 font-mono">{o.order_no}</td>
                        <td className="px-4 py-2">{o.company_name}</td>
                        <td className="px-4 py-2">{o.retailer_name}</td>
                        <td className="px-4 py-2 font-mono">{o.order_quantity}</td>
                        <td className="px-4 py-2 text-xs text-slate-600">{new Date(o.order_datetime).toLocaleString()}</td>
                      </tr>
                    ))}
                    {pending.length===0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">Nothing pending</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm mt-6">
              <div className="px-5 py-3 border-b border-slate-200"><div className="label-cap">Recently picked</div></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2">Order No</th><th className="px-4 py-2">Retailer</th>
                    <th className="px-4 py-2">Approx Box Qty</th><th className="px-4 py-2">Remarks</th>
                    <th className="px-4 py-2">Picked By</th><th className="px-4 py-2">Picked At</th>
                  </tr></thead>
                  <tbody>
                    {picked.map(o=>(
                      <tr key={o.id} className="border-b border-slate-100">
                        <td className="px-4 py-2 font-mono">{o.order_no}</td>
                        <td className="px-4 py-2">{o.retailer_name}</td>
                        <td className="px-4 py-2 font-mono">{o.picking?.approx_quantity ?? "—"}</td>
                        <td className="px-4 py-2 text-xs text-slate-600 max-w-xs">{o.picking?.remarks || "—"}</td>
                        <td className="px-4 py-2 text-xs">{nameFor(o.picking?.mobile)}</td>
                        <td className="px-4 py-2 text-xs text-slate-600">{o.picking && new Date(o.picking.at).toLocaleString()}</td>
                      </tr>
                    ))}
                    {picked.length===0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">—</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
